import React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Toolbar,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DynamicFormIcon from "@mui/icons-material/DynamicForm";
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
// import PaymentIcon from '@mui/icons-material/Payment';
// import ReceiptIcon from '@mui/icons-material/Receipt';
// import ListAltIcon from '@mui/icons-material/ListAlt';
import { useUserGroups } from "../../hooks/useUserGroups";

// Define menu items with permissions
const menuItems = [
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    allowedGroups: ["Admin", "User"], // Both Admins and Users can see Dashboard
  },
  {
    text: "Employees",
    icon: <PeopleIcon />,
    path: "/dashboard/employees",
    allowedGroups: ["Admin"], // Only Admins can see Employees
  },
  {
    text: "Locations",
    icon: <LocationOnIcon />,
    path: "/dashboard/locations",
    allowedGroups: ["Admin"], // Only Admins can see Locations
  },
  {
    text: "Forms",
    icon: <DynamicFormIcon />,
    path: "/dashboard/forms",
    allowedGroups: ["Admin"], // Only Admins can see Forms
  },
  {
    text: "Submissions",
    icon: <DynamicFormIcon />,
    path: "/dashboard/submissions",
    allowedGroups: ["Admin", "User"], // Both Admins and Users can see Submissions
  },
  // Timesheet Menu Items
  // {
  //   text: 'Timesheets',
  //   icon: <AccessTimeIcon />,
  //   path: '/dashboard/timesheets',
  //   allowedGroups: ['Admin', 'User'] // Both Admins and Users can access timesheets
  // },
  // {
  //   text: 'My Timesheet',
  //   icon: <ListAltIcon />,
  //   path: '/dashboard/timesheets/my-timesheet',
  //   allowedGroups: ['Admin', 'User'] // Both Admins and Users can access their timesheet
  // },
  // {
  //   text: 'Add Time Entry',
  //   icon: <AccessTimeIcon />,
  //   path: '/dashboard/timesheets/entry',
  //   allowedGroups: ['Admin', 'User'] // Both Admins and Users can add time entries
  // },
  // // Admin-only Timesheet Management
  // {
  //   text: 'Employee Timesheets',
  //   icon: <PeopleIcon />,
  //   path: '/dashboard/timesheets/employee-timesheets',
  //   allowedGroups: ['Admin'] // Only Admins can manage all employee timesheets
  // },
  // // Payroll Menu Items (Admin only)
  // {
  //   text: 'Payroll',
  //   icon: <PaymentIcon />,
  //   path: '/dashboard/payroll',
  //   allowedGroups: ['Admin'] // Only Admins can access payroll
  // },
  // {
  //   text: 'Payroll Runs',
  //   icon: <ListAltIcon />,
  //   path: '/dashboard/payroll/runs',
  //   allowedGroups: ['Admin'] // Only Admins can run payroll
  // },
  // {
  //   text: 'My Pay Stubs',
  //   icon: <ReceiptIcon />,
  //   path: '/dashboard/payroll/paystubs',
  //   allowedGroups: ['Admin', 'User'] // Both Admins and Users can view their pay stubs
  // },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userGroups, isAdmin, loading } = useUserGroups();

  // Show simplified loading state or nothing while checking groups
  if (loading) {
    return (
      <Box>
        <Toolbar />
        <Divider />
        <List>
          <ListItem>
            <ListItemText primary="Loading..." />
          </ListItem>
        </List>
      </Box>
    );
  }

  // Filter menu items based on user groups
  const filteredMenuItems = menuItems.filter((item) => {
    // If user is admin, show all items
    if (isAdmin) return true;

    // For non-admin users, check if they belong to any allowed group for this item
    return item.allowedGroups.some((allowedGroup) =>
      userGroups.includes(allowedGroup)
    );
  });

  // Group menu items by category for better organization
  const mainItems = filteredMenuItems.filter((item) =>
    ["Dashboard", "Employees", "Locations", "Forms", "Submissions"].includes(
      item.text
    )
  );

  const timesheetItems = filteredMenuItems.filter(
    (item) =>
      item.text.includes("Timesheet") || item.text.includes("Time Entry")
  );

  const payrollItems = filteredMenuItems.filter(
    (item) => item.text.includes("Payroll") || item.text.includes("Pay Stub")
  );

  return (
    <Box>
      <Toolbar />
      <Divider />

      {/* Main Navigation */}
      <List>
        {mainItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Timesheet Section */}
      {timesheetItems.length > 0 && (
        <>
          <Divider />
          <List>
            <ListItem>
              <ListItemText
                primary="Timesheets"
                primaryTypographyProps={{
                  variant: "subtitle2",
                  color: "text.secondary",
                  fontWeight: "bold",
                }}
              />
            </ListItem>
            {timesheetItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      {/* Payroll Section */}
      {payrollItems.length > 0 && (
        <>
          <Divider />
          <List>
            <ListItem>
              <ListItemText
                primary="Payroll"
                primaryTypographyProps={{
                  variant: "subtitle2",
                  color: "text.secondary",
                  fontWeight: "bold",
                }}
              />
            </ListItem>
            {payrollItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default Sidebar;
